/**
 * Run 16 item 2 (DECIDED 20261015, Barna): **a delivery or pickup time is
 * never shown without its date.**
 *
 *   > Wherever a delivery or pickup time is shown to the customer, show the
 *   > date and weekday with it, keeping the existing label:
 *   > HU *2026. október 22., csütörtök, 11:00–11:30*,
 *   > EN *Thursday 22 October 2026, 11:00–11:30*.
 *   > Use one shared formatter in the event's time zone (Europe/Budapest) for
 *   > every place.
 *
 * This is that formatter, and it lives in the engine because the three places
 * that need it — the customer page, the email templates and the Edge
 * Functions — already share this package and nothing else.
 *
 * **The zone.** A date is parsed out of `YYYY-MM-DD` by hand and the weekday
 * is worked out by Zeller's congruence; no `Date` is constructed from the
 * string, so no host zone can move the day and a date on either side of a DST
 * change reads the same. Where a caller holds an instant rather than a date —
 * the page's `sent.eventAt`, for instance — `budapestDate()` turns it into the
 * Budapest calendar date first, which is the one conversion there is.
 *
 * `format.ts` in `packages/emails` and `format.ts` in `apps/web/src/page` keep
 * their own date functions: they write the §5.12 short date (*2026. 10. 22.*)
 * and the long date for other lines. This file writes one line and only one:
 * the delivery or pickup window with the day it falls on.
 */

/** The two languages the page and §12's `page_languages` carry. */
export type EventTimeLang = 'hu' | 'en'

const WEEKDAYS_HU = [
  'vasárnap', 'hétfő', 'kedd', 'szerda', 'csütörtök', 'péntek', 'szombat',
] as const

const WEEKDAYS_EN = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
] as const

const MONTHS_HU = [
  'január', 'február', 'március', 'április', 'május', 'június',
  'július', 'augusztus', 'szeptember', 'október', 'november', 'december',
] as const

const MONTHS_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const

export interface DateParts {
  y: number
  m: number
  d: number
}

/**
 * `YYYY-MM-DD`, or a timestamp whose date part the database has already read
 * in Europe/Budapest. Parsed by hand: `new Date(...)` would read a bare date
 * as UTC and could move the day for a reader west of Greenwich.
 */
export function eventDateParts(value: string | null | undefined): DateParts | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ''))
  if (match === null) return null
  const y = Number(match[1])
  const m = Number(match[2])
  const d = Number(match[3])
  if (m < 1 || m > 12 || d < 1 || d > 31) return null
  return { y, m, d }
}

/** The day of the week, by Zeller, so no `Date` and no zone. 0 is Sunday. */
export function weekdayIndex(parts: DateParts): number {
  const { y, m, d } = parts
  const mm = m < 3 ? m + 12 : m
  const yy = m < 3 ? y - 1 : y
  const k = yy % 100
  const j = Math.floor(yy / 100)
  const h = (d + Math.floor((13 * (mm + 1)) / 5) + k + Math.floor(k / 4) + Math.floor(j / 4) + 5 * j) % 7
  // Zeller's h: 0 = Saturday. This array starts at Sunday.
  return (h + 6) % 7
}

/**
 * The date and the weekday, in the customer's language:
 * HU *2026. október 22., csütörtök*, EN *Thursday 22 October 2026*.
 *
 * The English has no comma after the weekday, which is the wording run 16
 * item 2 gives; `dtLong` in the page's own formatter writes *Thursday, 20
 * October 2026* for the other lines and is left as it is.
 */
export function eventDateLabel(lang: EventTimeLang, date: string | null | undefined): string {
  const parts = eventDateParts(date)
  if (parts === null) return ''
  const weekday = weekdayIndex(parts)
  if (lang === 'hu') {
    return `${parts.y}. ${MONTHS_HU[parts.m - 1]} ${parts.d}., ${WEEKDAYS_HU[weekday]}`
  }
  return `${WEEKDAYS_EN[weekday]} ${parts.d} ${MONTHS_EN[parts.m - 1]} ${parts.y}`
}

/** `08:30–10:30`, `11:00` and `11:00:00` all arrive; the line shows `HH:MM`. */
export function cleanWindow(window: string | null | undefined): string {
  const raw = String(window ?? '').trim()
  if (raw === '') return ''
  return raw.replace(/\b(\d{2}):(\d{2}):\d{2}\b/g, '$1:$2')
}

/**
 * **The one line run 16 item 2 asks for**: the date, the weekday and the time
 * window, in that order, separated by commas.
 *
 *   HU *2026. október 22., csütörtök, 11:00–11:30*
 *   EN *Thursday 22 October 2026, 11:00–11:30*
 *
 * A missing window gives the date and the weekday alone — which is what a
 * collection with no window in the register looks like — and a missing date
 * gives the window alone rather than a gap with a stray comma in it. Neither
 * invents anything: §5.2 allows an order with no slot.
 */
export function eventTimeLabel(
  lang: EventTimeLang,
  date: string | null | undefined,
  window: string | null | undefined,
): string {
  const day = eventDateLabel(lang, date)
  const time = cleanWindow(window)
  if (day === '') return time
  if (time === '') return day
  return `${day}, ${time}`
}

/**
 * An instant as the Budapest calendar reads it, `YYYY-MM-DD`.
 *
 * The only zone conversion in this file. The page holds instants (the event
 * and the collection both arrive as epoch milliseconds from the server) and
 * the visitor's browser may be anywhere, so the day is read in the event's own
 * zone before anything is printed (§10: every date in this system is read in
 * Europe/Budapest, never the reader's).
 */
export function budapestDate(instant: Date): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Budapest',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
      .formatToParts(instant)
      .map((part) => [part.type, part.value]),
  )
  return `${parts.year}-${parts.month}-${parts.day}`
}

/* ------------------------------------------------------------------ *
 * Run 19 §8 — a pickup that is not on the event date
 * ------------------------------------------------------------------ */

/**
 * Days from 1970-01-01 for a civil date, by Howard Hinnant's `days_from_civil`.
 * No `Date`, so no zone and no DST: two calendar dates are subtracted as
 * calendar dates, which is what §8's *calendar days* means.
 */
function dayNumber(parts: DateParts): number {
  const y = parts.m <= 2 ? parts.y - 1 : parts.y
  const era = Math.floor((y >= 0 ? y : y - 399) / 400)
  const yoe = y - era * 400
  const mp = (parts.m + 9) % 12
  const doy = Math.floor((153 * mp + 2) / 5) + parts.d - 1
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy
  return era * 146097 + doe - 719468
}

/**
 * Whole calendar days from `from` to `to`, or null where either is not a
 * date. Negative where `to` is the earlier of the two.
 */
export function calendarDaysBetween(
  from: string | null | undefined,
  to: string | null | undefined,
): number | null {
  const a = eventDateParts(from)
  const b = eventDateParts(to)
  if (a === null || b === null) return null
  return dayNumber(b) - dayNumber(a)
}

/**
 * **Run 19 §8 (DECIDED 20261016, Barna).** How far before the event the food
 * is collected, where that is not the event day at all.
 *
 * `null` means there is nothing to highlight: the collection is on the event
 * date, one of the two dates is missing, or the collection is *after* the
 * event, which no rule produces and which this function will not describe.
 *
 * A pickup on a day other than the event date is the costliest thing on the
 * page to misread — a customer who turns up on the Saturday for food that
 * was ready on the Friday has no event — so every surface that shows the
 * pickup time shows this beside it.
 */
export function pickupDaysBefore(
  pickupDate: string | null | undefined,
  eventDate: string | null | undefined,
): number | null {
  const days = calendarDaysBetween(pickupDate, eventDate)
  if (days === null || days <= 0) return null
  return days
}

/**
 * §8's sentence, in one place, for the page, the emails, the PDFs, the
 * kitchen output and the console.
 *
 *   1 day  HU *Átvétel: 2026. október 23., péntek, 16:30-ig, a rendezvény
 *             előtti napon.*
 *          EN *Pickup: Friday 23 October 2026, by 16:30, the day before your
 *             event.*
 *   2+     HU *… 2 nappal a rendezvény előtt.*
 *          EN *… 2 days before your event.*
 *
 * `by` is `pickup_to` out of the register and is passed in, never held here.
 * An empty one drops the clause rather than printing a gap, which is what a
 * register with no collection end looks like.
 */
export function earlyPickupNotice(
  lang: EventTimeLang,
  args: {
    pickupDate: string | null | undefined
    eventDate: string | null | undefined
    pickupTo?: string | null
  },
): string | null {
  const days = pickupDaysBefore(args.pickupDate, args.eventDate)
  if (days === null) return null
  const day = eventDateLabel(lang, args.pickupDate)
  if (day === '') return null
  const by = cleanWindow(args.pickupTo).trim()
  if (lang === 'en') {
    const when = days === 1 ? 'the day before your event' : `${days} days before your event`
    return by === ''
      ? `Pickup: ${day}, ${when}.`
      : `Pickup: ${day}, by ${by}, ${when}.`
  }
  const when = days === 1 ? 'a rendezvény előtti napon' : `${days} nappal a rendezvény előtt`
  return by === ''
    ? `Átvétel: ${day}, ${when}.`
    : `Átvétel: ${day}, ${by}-ig, ${when}.`
}

/**
 * §8 again, for the kitchen output and the console: the same date, named as
 * the **production deadline**, so nobody reads the event date as the day the
 * food has to be finished.
 */
export function earlyPickupKitchenNotice(
  lang: EventTimeLang,
  args: {
    pickupDate: string | null | undefined
    eventDate: string | null | undefined
    pickupTo?: string | null
  },
): string | null {
  const days = pickupDaysBefore(args.pickupDate, args.eventDate)
  if (days === null) return null
  const day = eventDateLabel(lang, args.pickupDate)
  if (day === '') return null
  const by = cleanWindow(args.pickupTo).trim()
  if (lang === 'en') {
    const when = days === 1 ? 'the day before the event' : `${days} days before the event`
    return by === ''
      ? `Production deadline: ${day} — collection is ${when}, not on the event date.`
      : `Production deadline: ${day}, by ${by} — collection is ${when}, not on the event date.`
  }
  const when = days === 1 ? 'a rendezvény előtti napon' : `${days} nappal a rendezvény előtt`
  return by === ''
    ? `Gyártási határidő: ${day} — az átvétel ${when} van, nem a rendezvény napján.`
    : `Gyártási határidő: ${day}, ${by}-ig — az átvétel ${when} van, nem a rendezvény napján.`
}
